import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { aiAdminApi, liveSettingsKeys } from '../../../services/ai-admin-api';
import {
    Alert,
    Button,
    Card,
    Empty,
    Form,
    Input,
    InputNumber,
    Modal,
    Select,
    Skeleton,
    Space,
    Switch,
    Table,
    Tabs,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd';
import {
    CheckOutlined,
    CodeOutlined,
    PlusOutlined,
    StopOutlined,
} from '@ant-design/icons';
import { useSubsystemEditorContext } from '../hooks/useSubsystemDraftEditor';
import { SubsystemDraftActions } from '../components/SubsystemDraftActions';
import { PromptEditor } from '../shared/PromptEditor';
import { ProviderId, ProviderModelTempForm, ProviderModelTempValue } from '../shared/ProviderModelTempForm';

const { Text, Title } = Typography;

interface AiExpertRow {
    id?: string;
    expert_type: string;
    name: string;
    specialty?: string;
    description?: string;
    persona_prompt: string;
    expertise?: string[] | string | null;
    color?: string;
    icon?: string;
    is_active: boolean | number;
    search_filters?: Record<string, unknown> | string | null;
    required_subscription_level?: string | null;
    knowledge_base_id?: string | null;
    llm_temperature?: number | string | null;
    llm_model?: string | null;
    [k: string]: unknown;
}

function parseExpertiseToArray(v: unknown): string[] {
    if (Array.isArray(v)) return v.map(String);
    if (typeof v === 'string' && v.trim()) {
        try {
            const parsed = JSON.parse(v);
            if (Array.isArray(parsed)) return parsed.map(String);
        } catch {
            /* fall through */
        }
    }
    return [];
}

function searchFiltersToText(v: unknown): string {
    if (v === null || v === undefined || v === '') return '';
    if (typeof v === 'string') return v;
    return JSON.stringify(v, null, 2);
}

interface DashboardAiConfigRow {
    id?: number;
    expert_type: string;
    dashboard_prompt: string;
    /** Optional override for the dashboard AI's role/persona prompt. Falls back to a hardcoded default if null. */
    system_prompt?: string | null;
    is_active?: boolean | number;
    [k: string]: unknown;
}

interface AiExpertsPayload {
    schemaVersion: 1;
    systemSettingsKeys: Record<string, string | undefined>;
    configTableRows: {
        ai_experts?: AiExpertRow[];
        dashboard_ai_config?: DashboardAiConfigRow[];
    };
}


const SUBSCRIPTION_OPTIONS = [
    { value: '', label: '(no minimum)' },
    { value: 'professional', label: 'Professional' },
    { value: 'enterprise', label: 'Enterprise' },
];

function isActiveBool(v: boolean | number | undefined): boolean {
    return v === true || v === 1;
}

/**
 * Configure tab body for AI Experts.
 *
 * Left list of experts; right detail panel with Persona / Dashboard /
 * Behaviour / Chat limits tabs. The dashboard prompt is read from the
 * dashboard_ai_config row whose expert_type matches; toggles cascade through
 * the same payload draft as everything else, so one Save Draft + Publish
 * commits the whole expert config atomically (system_settings + experts +
 * dashboard configs).
 */
export const AiExpertsConfigurePage: React.FC = () => {
    const editor = useSubsystemEditorContext<AiExpertsPayload>();
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const [selectedExpertType, setSelectedExpertType] = useState<string | null>(null);
    const [adding, setAdding] = useState(false);
    const [anthropicModels, setAnthropicModels] = useState<Array<{ id: string; label?: string }>>([]);
    const [openaiModels, setOpenaiModels] = useState<Array<{ id: string; label?: string }>>([]);
    const [googleModels, setGoogleModels] = useState<Array<{ id: string; label?: string }>>([]);
    // Deep-link support: ?section=defaults lands the user on the Platform defaults
    // tab so links from the System Settings AI System tab go straight to the right
    // editor. Read once at mount; clicks afterwards control the tab locally.
    const [searchParams] = useSearchParams();
    const initialSection = searchParams.get('section') === 'defaults' ? 'defaults' : 'experts';
    const [embeddingModels, setEmbeddingModels] = useState<Array<{ id: string; label?: string }>>([]);

    useEffect(() => {
        // Model lists live in the models_providers subsystem — fetch its detail
        // so the per-expert model dropdowns populate.
        if (!jwt) return;
        aiAdminApi
            .getSubsystem(jwt, 'models_providers')
            .then((mp) => {
                const keys = liveSettingsKeys(mp);
                setAnthropicModels(
                    parseModelList(keys.AVAILABLE_ANTHROPIC_MODELS),
                );
                setOpenaiModels(
                    parseModelList(keys.AVAILABLE_OPENAI_MODELS),
                );
                setGoogleModels(
                    parseModelList(keys.AVAILABLE_GOOGLE_MODELS),
                );
                setEmbeddingModels(
                    parseModelList(keys.AVAILABLE_EMBEDDING_MODELS),
                );
            })
            .catch(() => {
                // Non-fatal — admin can still type a model id manually.
            });
    }, [jwt]);

    const experts = editor.payload?.configTableRows?.ai_experts ?? [];
    const dashboardRows = editor.payload?.configTableRows?.dashboard_ai_config ?? [];

    // Default selection — first expert.
    const effectiveSelected = selectedExpertType ?? experts[0]?.expert_type ?? null;
    const selected = experts.find((e) => e.expert_type === effectiveSelected) ?? null;
    const selectedDashboard = dashboardRows.find((r) => r.expert_type === effectiveSelected) ?? null;

    if (editor.isLoading || !editor.payload) {
        return <Skeleton active paragraph={{ rows: 10 }} />;
    }

    const setExperts = (next: AiExpertRow[]) => {
        editor.setPayload((prev) => ({
            ...prev!,
            configTableRows: {
                ...prev!.configTableRows,
                ai_experts: next,
            },
        }));
    };

    const setDashboardRows = (next: DashboardAiConfigRow[]) => {
        editor.setPayload((prev) => ({
            ...prev!,
            configTableRows: {
                ...prev!.configTableRows,
                dashboard_ai_config: next,
            },
        }));
    };

    const setSystemSetting = (key: string, value: string) => {
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, [key]: value },
        }));
    };

    const updateExpert = (expertType: string, patch: Partial<AiExpertRow>) => {
        setExperts(
            experts.map((e) =>
                e.expert_type === expertType ? { ...e, ...patch } : e,
            ),
        );
    };

    const updateDashboard = (expertType: string, patch: Partial<DashboardAiConfigRow>) => {
        const existing = dashboardRows.find((r) => r.expert_type === expertType);
        if (existing) {
            setDashboardRows(
                dashboardRows.map((r) =>
                    r.expert_type === expertType ? { ...r, ...patch } : r,
                ),
            );
        } else {
            setDashboardRows([
                ...dashboardRows,
                {
                    expert_type: expertType,
                    dashboard_prompt: patch.dashboard_prompt ?? '',
                    is_active: 1,
                    ...patch,
                },
            ]);
        }
    };

    const addExpert = (entry: AiExpertRow) => {
        if (experts.some((e) => e.expert_type === entry.expert_type)) {
            message.error('An expert with that expert_type already exists.');
            return false;
        }
        setExperts([...experts, entry]);
        setSelectedExpertType(entry.expert_type);
        return true;
    };

    return (
        <div>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
                <Text type="secondary">
                    Per-expert persona, dashboard prompts, model behaviour, and chat rate limits.
                    Removing an expert from the table is intentionally disabled — set
                    Active = off to retire one without losing conversation history.
                </Text>
                <SubsystemDraftActions
                    detail={editor.detail}
                    isDirty={editor.isDirty}
                    isSaving={editor.isSaving}
                    isPublishing={editor.isPublishing}
                    isDiscarding={editor.isDiscarding}
                    onSaveDraft={() => editor.saveDraft()}
                    onDiscardDraft={() => editor.discardDraft()}
                />
            </Space>


            <Tabs
                defaultActiveKey={initialSection}
                items={[
                    {
                        key: 'experts',
                        label: 'Per-expert',
                        children: (
                            <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 16 }}>{renderExpertsBody()}</div>
                        ),
                    },
                    {
                        key: 'defaults',
                        label: 'Platform defaults',
                        children: (
                            <PlatformDefaultsForm
                                payload={editor.payload!}
                                anthropicModels={anthropicModels}
                                openaiModels={openaiModels}
                                googleModels={googleModels}
                                embeddingModels={embeddingModels}
                                onSetSetting={setSystemSetting}
                            />
                        ),
                    },
                ]}
            />

            <AddExpertModal
                open={adding}
                onCancel={() => setAdding(false)}
                onAdd={(entry) => {
                    const ok = addExpert(entry);
                    if (ok) setAdding(false);
                }}
            />
        </div>
    );

    function renderExpertsBody() {
        return (
            <>
                <Card
                    size="small"
                    title="Experts"
                    extra={
                        <Button
                            type="primary"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => setAdding(true)}
                        >
                            Add
                        </Button>
                    }
                    styles={{ body: { padding: 0 } }}
                >
                    <Table<AiExpertRow & { __key: string }>
                        size="small"
                        rowKey="__key"
                        showHeader={false}
                        pagination={false}
                        dataSource={experts.map((e) => ({ ...e, __key: e.expert_type }))}
                        rowClassName={(row) =>
                            row.expert_type === effectiveSelected ? 'ant-table-row-selected' : ''
                        }
                        onRow={(row) => ({
                            onClick: () => setSelectedExpertType(row.expert_type),
                            style: { cursor: 'pointer' },
                        })}
                        columns={[
                            {
                                title: 'Expert',
                                key: 'name',
                                render: (_v: unknown, row) => (
                                    <Space direction="vertical" size={0}>
                                        <Text strong>{row.name}</Text>
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            <code>{row.expert_type}</code>
                                        </Text>
                                    </Space>
                                ),
                            },
                            {
                                key: 'active',
                                width: 60,
                                render: (_v: unknown, row) =>
                                    isActiveBool(row.is_active) ? (
                                        <Tag color="green">on</Tag>
                                    ) : (
                                        <Tag>off</Tag>
                                    ),
                            },
                        ]}
                        locale={{
                            emptyText: <Empty description="No experts yet — click Add." />,
                        }}
                    />
                </Card>

                <div>
                    {!selected ? (
                        <Empty description="Select an expert from the list, or add one." />
                    ) : (
                        <ExpertDetail
                            expert={selected}
                            dashboard={selectedDashboard}
                            anthropicModels={anthropicModels}
                            openaiModels={openaiModels}
                            googleModels={googleModels}
                            onChangeExpert={(patch) => updateExpert(selected.expert_type, patch)}
                            onChangeDashboard={(patch) => updateDashboard(selected.expert_type, patch)}
                        />
                    )}
                </div>
            </>
        );
    }
};

const ExpertDetail: React.FC<{
    expert: AiExpertRow;
    dashboard: DashboardAiConfigRow | null;
    anthropicModels: Array<{ id: string; label?: string }>;
    openaiModels: Array<{ id: string; label?: string }>;
    googleModels: Array<{ id: string; label?: string }>;
    onChangeExpert: (patch: Partial<AiExpertRow>) => void;
    onChangeDashboard: (patch: Partial<DashboardAiConfigRow>) => void;
}> = ({
    expert,
    dashboard,
    anthropicModels,
    openaiModels,
    googleModels,
    onChangeExpert,
    onChangeDashboard,
}) => {
    const guessProvider = (modelId: string | null | undefined): ProviderId => {
        if (!modelId) return 'anthropic';
        if (modelId.startsWith('claude') || modelId.startsWith('anthropic')) return 'anthropic';
        if (modelId.startsWith('gemini') || modelId.startsWith('google')) return 'google';
        return 'openai';
    };
    const provider = guessProvider(expert.llm_model);
    const tempValue: ProviderModelTempValue = {
        provider,
        model: expert.llm_model ?? '',
        temperature: Number(expert.llm_temperature ?? 0.7),
    };

    return (
        <Card>
            <Space style={{ marginBottom: 12 }}>
                <Title level={4} style={{ margin: 0 }}>
                    {expert.name}
                </Title>
                <Tag color={isActiveBool(expert.is_active) ? 'green' : 'default'}>
                    {isActiveBool(expert.is_active) ? 'active' : 'inactive'}
                </Tag>
                <Tooltip title={isActiveBool(expert.is_active) ? 'Retire this expert' : 'Re-activate this expert'}>
                    <Button
                        size="small"
                        icon={isActiveBool(expert.is_active) ? <StopOutlined /> : <CheckOutlined />}
                        onClick={() => {
                            const turningOn = !isActiveBool(expert.is_active);
                            // Use 1/0 to match how MySQL TINYINT(1) round-trips to
                            // the payload — sending a JS boolean creates a phantom
                            // type-only diff vs the existing number value.
                            onChangeExpert({ is_active: turningOn ? 1 : 0 });
                            message.info(
                                `${expert.name} marked ${turningOn ? 'active' : 'inactive'} in the draft. Click Save Draft → Publish to apply to the customer dashboard.`,
                                6,
                            );
                        }}
                    >
                        {isActiveBool(expert.is_active) ? 'Deactivate' : 'Activate'}
                    </Button>
                </Tooltip>
            </Space>

            <Tabs
                items={[
                    {
                        key: 'persona',
                        label: 'Persona',
                        children: (
                            <Form layout="vertical">
                                <Form.Item label="Display name">
                                    <Input
                                        value={expert.name}
                                        onChange={(e) =>
                                            onChangeExpert({ name: e.target.value })
                                        }
                                    />
                                </Form.Item>
                                <Form.Item label="Specialty (one-liner)">
                                    <Input
                                        value={expert.specialty ?? ''}
                                        onChange={(e) =>
                                            onChangeExpert({ specialty: e.target.value })
                                        }
                                    />
                                </Form.Item>
                                <Form.Item label="Description">
                                    <Input.TextArea
                                        rows={2}
                                        value={expert.description ?? ''}
                                        onChange={(e) =>
                                            onChangeExpert({ description: e.target.value })
                                        }
                                    />
                                </Form.Item>
                                <Form.Item label="Expertise tags (shown on the chooser tile)">
                                    <Select
                                        mode="tags"
                                        style={{ width: '100%' }}
                                        value={parseExpertiseToArray(expert.expertise)}
                                        onChange={(tags: string[]) =>
                                            onChangeExpert({ expertise: tags })
                                        }
                                        placeholder="Brand Equity Theory, Consumer Behavior, …"
                                        tokenSeparators={[',']}
                                    />
                                </Form.Item>
                                <Space style={{ width: '100%' }}>
                                    <Form.Item label="Tile background (Tailwind class)" style={{ flex: 1, minWidth: 240 }}>
                                        <Input
                                            value={expert.color ?? ''}
                                            onChange={(e) =>
                                                onChangeExpert({ color: e.target.value })
                                            }
                                            placeholder="bg-blue-500"
                                        />
                                    </Form.Item>
                                    <Form.Item label="Tile icon (lucide.dev name)" style={{ flex: 1, minWidth: 240 }}>
                                        <Input
                                            value={expert.icon ?? ''}
                                            onChange={(e) =>
                                                onChangeExpert({ icon: e.target.value })
                                            }
                                            placeholder="GraduationCap"
                                        />
                                    </Form.Item>
                                </Space>
                                <Form.Item label="Persona prompt">
                                    <PromptEditor
                                        consumerSubsystem="ai_experts"
                                        value={expert.persona_prompt ?? ''}
                                        onChange={(v) => onChangeExpert({ persona_prompt: v })}
                                        rows={14}
                                    />
                                </Form.Item>
                            </Form>
                        ),
                    },
                    {
                        key: 'dashboard',
                        label: 'Dashboard extract',
                        children: (
                            <Form layout="vertical">
                                <Alert
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 12 }}
                                    message={`The 50-word "first thoughts" the expert produces alongside dashboard scores. Stored in dashboard_ai_config keyed by expert_type.`}
                                />
                                <Form.Item label="System prompt (role / persona)">
                                    <PromptEditor
                                        consumerSubsystem="ai_experts"
                                        value={dashboard?.system_prompt ?? ''}
                                        onChange={(v) =>
                                            onChangeDashboard({ system_prompt: v })
                                        }
                                        placeholder="Defines the expert's role/voice for the dashboard extract. Leave blank to use the hardcoded default."
                                        rows={10}
                                    />
                                </Form.Item>
                                <Form.Item label="Dashboard prompt (user message)">
                                    <PromptEditor
                                        consumerSubsystem="ai_experts"
                                        value={dashboard?.dashboard_prompt ?? ''}
                                        onChange={(v) =>
                                            onChangeDashboard({ dashboard_prompt: v })
                                        }
                                        placeholder="The instruction sent alongside the brand equity data."
                                        rows={10}
                                    />
                                </Form.Item>
                            </Form>
                        ),
                    },
                    {
                        key: 'behaviour',
                        label: 'Behaviour',
                        children: (
                            <Form layout="vertical">
                                <Form.Item label="LLM provider / model / temperature (per-expert override)">
                                    <ProviderModelTempForm
                                        value={tempValue}
                                        onChange={(v) =>
                                            onChangeExpert({
                                                llm_model: v.model || null,
                                                llm_temperature: v.temperature,
                                            })
                                        }
                                        anthropicModels={anthropicModels}
                                        openaiModels={openaiModels}
                                        googleModels={googleModels}
                                        allowNoModel
                                    />
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        Pick "(use platform-wide default)" to fall back to
                                        AI_EXPERT_PRIMARY_MODEL.
                                    </Text>
                                </Form.Item>
                                <Form.Item label="Knowledge base id">
                                    <Input
                                        value={expert.knowledge_base_id ?? ''}
                                        onChange={(e) =>
                                            onChangeExpert({
                                                knowledge_base_id:
                                                    e.target.value.trim() || null,
                                            })
                                        }
                                        placeholder="default"
                                    />
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        Optional Pinecone namespace. Blank = shared default.
                                    </Text>
                                </Form.Item>
                                <Form.Item label="Required subscription level">
                                    <Select
                                        value={expert.required_subscription_level ?? ''}
                                        onChange={(v) =>
                                            onChangeExpert({
                                                required_subscription_level: v || null,
                                            })
                                        }
                                        options={SUBSCRIPTION_OPTIONS}
                                    />
                                </Form.Item>
                                <Form.Item label="Search filters (JSON, affects RAG retrieval)">
                                    <SearchFiltersEditor
                                        value={expert.search_filters}
                                        onChange={(v) =>
                                            onChangeExpert({ search_filters: v })
                                        }
                                    />
                                </Form.Item>
                                <Form.Item label="Active">
                                    <Switch
                                        checked={isActiveBool(expert.is_active)}
                                        onChange={(v) =>
                                            onChangeExpert({ is_active: v ? 1 : 0 })
                                        }
                                    />
                                </Form.Item>
                            </Form>
                        ),
                    },
                ]}
            />
        </Card>
    );
};

const AddExpertModal: React.FC<{
    open: boolean;
    onCancel: () => void;
    onAdd: (entry: AiExpertRow) => void;
}> = ({ open, onCancel, onAdd }) => {
    const [draft, setDraft] = useState<AiExpertRow>({
        expert_type: '',
        name: '',
        specialty: '',
        description: '',
        persona_prompt: '',
        color: 'bg-blue-500',
        icon: 'GraduationCap',
        is_active: 1,
        llm_temperature: 0.7,
    });

    const submit = () => {
        if (!draft.expert_type.trim() || !draft.name.trim()) {
            message.error('expert_type and name are required.');
            return;
        }
        if (!/^[a-z][a-z0-9_]+$/.test(draft.expert_type)) {
            message.error('expert_type must be lower_snake_case.');
            return;
        }
        onAdd(draft);
        setDraft({
            expert_type: '',
            name: '',
            specialty: '',
            description: '',
            persona_prompt: '',
            color: 'bg-blue-500',
            icon: 'GraduationCap',
            is_active: 1,
            llm_temperature: 0.7,
        });
    };

    return (
        <Modal
            open={open}
            title="Add new expert"
            onCancel={onCancel}
            onOk={submit}
            okText="Add (still need to Save draft + Publish)"
            width={640}
        >
            <Form layout="vertical">
                <Form.Item
                    label="expert_type (lower_snake_case, immutable runtime key)"
                    required
                >
                    <Input
                        value={draft.expert_type}
                        onChange={(e) =>
                            setDraft((d) => ({ ...d, expert_type: e.target.value }))
                        }
                        placeholder="brand_strategy_consultant"
                    />
                </Form.Item>
                <Form.Item label="Display name" required>
                    <Input
                        value={draft.name}
                        onChange={(e) =>
                            setDraft((d) => ({ ...d, name: e.target.value }))
                        }
                        placeholder="Brand Strategy Consultant"
                    />
                </Form.Item>
                <Form.Item label="Specialty">
                    <Input
                        value={draft.specialty}
                        onChange={(e) =>
                            setDraft((d) => ({ ...d, specialty: e.target.value }))
                        }
                        placeholder="Strategic positioning & growth"
                    />
                </Form.Item>
                <Form.Item label="Description">
                    <Input.TextArea
                        rows={2}
                        value={draft.description}
                        onChange={(e) =>
                            setDraft((d) => ({ ...d, description: e.target.value }))
                        }
                    />
                </Form.Item>
                <Form.Item label="Persona prompt" required>
                    <Input.TextArea
                        rows={6}
                        value={draft.persona_prompt}
                        onChange={(e) =>
                            setDraft((d) => ({ ...d, persona_prompt: e.target.value }))
                        }
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

/**
 * Compact JSON editor for `ai_experts.search_filters`. Validates parse on
 * every keystroke; "Format" button pretty-prints; emits the parsed object
 * up to the parent (so the payload stays a real object, not a stringified
 * blob, for diff clarity).
 */
const SearchFiltersEditor: React.FC<{
    value: AiExpertRow['search_filters'];
    onChange: (next: Record<string, unknown> | null) => void;
}> = ({ value, onChange }) => {
    const [text, setText] = useState<string>(searchFiltersToText(value));
    const [parseError, setParseError] = useState<string | null>(null);

    // Re-sync local text if the source changes from outside (e.g. switching expert).
    React.useEffect(() => {
        setText(searchFiltersToText(value));
        setParseError(null);
    }, [value]);

    const commit = (next: string) => {
        setText(next);
        const trimmed = next.trim();
        if (!trimmed) {
            setParseError(null);
            onChange(null);
            return;
        }
        try {
            const parsed = JSON.parse(trimmed);
            if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
                setParseError('search_filters must be a JSON object.');
                return;
            }
            setParseError(null);
            onChange(parsed as Record<string, unknown>);
        } catch (err) {
            setParseError((err as Error).message);
            // Don't push invalid JSON to the payload; keep the previous parsed value.
        }
    };

    const formatNow = () => {
        const trimmed = text.trim();
        if (!trimmed) return;
        try {
            const parsed = JSON.parse(trimmed);
            commit(JSON.stringify(parsed, null, 2));
        } catch {
            /* parse error already shown */
        }
    };

    return (
        <div>
            <Space style={{ marginBottom: 4 }}>
                <Tag color={parseError ? 'red' : text.trim() ? 'green' : 'default'}>
                    {parseError ? 'invalid JSON' : text.trim() ? 'JSON' : 'empty'}
                </Tag>
                <Button
                    size="small"
                    icon={<CodeOutlined />}
                    onClick={formatNow}
                    disabled={!!parseError || !text.trim()}
                >
                    Format JSON
                </Button>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    Object of retrieval hints (e.g. {`{"prefer_academic": true, "confidence_threshold": 0.7}`}).
                </Text>
            </Space>
            <Input.TextArea
                rows={6}
                value={text}
                onChange={(e) => commit(e.target.value)}
                style={{ fontFamily: 'monospace' }}
                placeholder='{"prefer_academic": true, "confidence_threshold": 0.7}'
            />
            {parseError && (
                <Alert
                    style={{ marginTop: 4 }}
                    type="warning"
                    showIcon
                    message="JSON parse error"
                    description={parseError}
                />
            )}
        </div>
    );
};

/**
 * Platform-wide settings for the AI Experts subsystem. Edits here are applied
 * to ALL experts unless they have a per-expert override.
 *
 * Mirrors the layout of the legacy /admin/ai-experts/dashboard-ai page (Primary
 * + Fallback sections with provider/model/temperature triplets) plus extras
 * for embedding model and chat rate limits.
 */
const PlatformDefaultsForm: React.FC<{
    payload: AiExpertsPayload;
    anthropicModels: Array<{ id: string; label?: string }>;
    openaiModels: Array<{ id: string; label?: string }>;
    googleModels: Array<{ id: string; label?: string }>;
    embeddingModels: Array<{ id: string; label?: string }>;
    onSetSetting: (key: string, value: string) => void;
}> = ({ payload, anthropicModels, openaiModels, googleModels, embeddingModels, onSetSetting }) => {
    const get = (key: string) => payload.systemSettingsKeys?.[key] ?? '';
    const renderTriplet = (
        label: string,
        providerKey: string,
        modelKey: string,
        tempKey: string,
    ) => (
        <Card size="small" title={label} style={{ marginBottom: 12 }}>
            <ProviderModelTempForm
                value={{
                    provider: (get(providerKey) || 'anthropic') as ProviderId,
                    model: get(modelKey),
                    temperature: Number(get(tempKey) || 0.7),
                }}
                onChange={(v) => {
                    onSetSetting(providerKey, v.provider);
                    onSetSetting(modelKey, v.model);
                    onSetSetting(tempKey, String(v.temperature));
                }}
                anthropicModels={anthropicModels}
                openaiModels={openaiModels}
                googleModels={googleModels}
            />
        </Card>
    );

    return (
        <div>
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Platform-wide AI defaults"
                description="These settings apply to every expert that doesn't have a per-expert override. Per-expert behaviour (model, temperature, knowledge base) lives on each expert's Behaviour tab."
            />

            <Title level={4}>AI Experts (chat)</Title>
            {renderTriplet(
                'Primary provider — used by default for every expert chat call',
                'AI_EXPERT_PRIMARY_PROVIDER',
                'AI_EXPERT_PRIMARY_MODEL',
                'AI_EXPERT_PRIMARY_TEMPERATURE',
            )}
            {renderTriplet(
                'Fallback provider — automatic failover if the primary errors',
                'AI_EXPERT_FALLBACK_PROVIDER',
                'AI_EXPERT_FALLBACK_MODEL',
                'AI_EXPERT_FALLBACK_TEMPERATURE',
            )}

            <Card size="small" title="Embedding model" style={{ marginBottom: 24 }}>
                <Form layout="vertical">
                    <Form.Item label="AI_EXPERT_EMBEDDING_MODEL">
                        <Select
                            style={{ width: '100%' }}
                            value={get('AI_EXPERT_EMBEDDING_MODEL') || undefined}
                            onChange={(v) =>
                                onSetSetting('AI_EXPERT_EMBEDDING_MODEL', v ?? '')
                            }
                            options={embeddingModels.map((m) => ({
                                value: m.id,
                                label: m.label ?? m.id,
                            }))}
                            placeholder="text-embedding-3-large"
                            showSearch
                            optionFilterProp="label"
                            allowClear
                        />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Pulled from the Models & Providers subsystem's Embedding tab.
                        </Text>
                    </Form.Item>
                </Form>
            </Card>

            <Title level={4}>Dashboard extract</Title>
            {renderTriplet(
                'Primary provider — used for the per-expert dashboard "first thoughts" extracts',
                'DASHBOARD_AI_PRIMARY_PROVIDER',
                'DASHBOARD_AI_PRIMARY_MODEL',
                'DASHBOARD_AI_PRIMARY_TEMPERATURE',
            )}
            {renderTriplet(
                'Fallback provider — automatic failover for dashboard extracts',
                'DASHBOARD_AI_FALLBACK_PROVIDER',
                'DASHBOARD_AI_FALLBACK_MODEL',
                'DASHBOARD_AI_FALLBACK_TEMPERATURE',
            )}

            <Title level={4}>Chat rate limits</Title>
            <Card size="small" style={{ marginBottom: 12 }}>
                <Form layout="vertical">
                    <Form.Item label="Hourly chat limit per user (AI_EXPERT_CHAT_HOURLY_LIMIT)">
                        <InputNumber
                            min={0}
                            style={{ width: 240 }}
                            value={Number(get('AI_EXPERT_CHAT_HOURLY_LIMIT')) || 0}
                            onChange={(v) =>
                                onSetSetting('AI_EXPERT_CHAT_HOURLY_LIMIT', String(v ?? ''))
                            }
                        />
                    </Form.Item>
                    <Form.Item label="Daily chat limit per user (AI_EXPERT_CHAT_DAILY_LIMIT)">
                        <InputNumber
                            min={0}
                            style={{ width: 240 }}
                            value={Number(get('AI_EXPERT_CHAT_DAILY_LIMIT')) || 0}
                            onChange={(v) =>
                                onSetSetting('AI_EXPERT_CHAT_DAILY_LIMIT', String(v ?? ''))
                            }
                        />
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

function parseModelList(json: string | undefined): Array<{ id: string; label?: string }> {
    if (!json) return [];
    try {
        const parsed = JSON.parse(json);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}
